import { format, isValid, parse, parseISO } from 'date-fns';
import { htmlToPlainText } from '../../utils/commonUtils';

export const SECTION = {
    summary: 'summary',
    organisation: 'organisation',
    engagement: 'engagement',
    publicProfiles: 'publicProfiles',
    leadership: 'leadership',
    technology: 'technology',
    competitors: 'competitors',
    configuration: 'configuration',
};

export const LEADERSHIP_ROLES = [
    { key: 'ceo', label: 'CEO' },
    { key: 'coo', label: 'COO' },
    { key: 'cto', label: 'CTO' },
    { key: 'cpo', label: 'CPO' },
];

export const PUBLIC_PROFILES = [
    { key: 'linkedin', label: 'LinkedIn' },
    { key: 'crunchbase', label: 'Crunchbase' },
    { key: 'glassdoor', label: 'Glassdoor' },
    { key: 'endole', label: 'Endole' },
];

export const TECHNOLOGY_LINKS = [
    { key: 'builtwith', label: 'BuiltWith' },
    { key: 'securitytrails', label: 'SecurityTrails' },
    { key: 'gtmetrix', label: 'GTMetrix' },
    { key: 'similarweb', label: 'Similarweb' },
];

export const CURRENCIES = [
    { code: 'GBP', symbol: '£' },
    { code: 'USD', symbol: '$' },
    { code: 'EUR', symbol: '€' },
];

const PROJECT_UPDATE_EMAIL = 'weekly_summary_email';
const REVIEW_REMINDER_EMAIL = 'daily_irls_email';
const EMPTY_MARKERS = new Set(['', '--', '––', '-', '—']);

export function hasValue(value) {
    return !EMPTY_MARKERS.has(String(value ?? '').trim());
}

export function displayValue(value) {
    return hasValue(value) ? String(value).trim() : '—';
}

export function toPlainText(value) {
    if (!hasValue(value)) return '';
    const text = String(value);
    if (typeof document === 'undefined') {
        return text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    }
    return htmlToPlainText(text).replace(/\u00a0/g, ' ').trim();
}

export function parseProjectDate(value) {
    if (!value) return null;
    const text = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(text)) {
        const parsed = parseISO(text.slice(0, 10));
        return isValid(parsed) ? parsed : null;
    }
    const dayMonthYear = text.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
    if (dayMonthYear) {
        const [, day, month, year] = dayMonthYear;
        const parsed = parse(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`, 'yyyy-MM-dd', new Date());
        return isValid(parsed) ? parsed : null;
    }
    const parsed = new Date(text);
    return isValid(parsed) ? parsed : null;
}

export function formatDisplayDate(value) {
    const parsed = parseProjectDate(value);
    return parsed ? format(parsed, 'd MMM yyyy') : '—';
}

export function toDateInputValue(value) {
    const parsed = parseProjectDate(value);
    return parsed ? format(parsed, 'yyyy-MM-dd') : '';
}

export function currencyCode(value) {
    return CURRENCIES.find(item => item.symbol === value || item.code === value)?.code || 'GBP';
}

export function currencySymbol(value) {
    return CURRENCIES.find(item => item.code === value || item.symbol === value)?.symbol || '£';
}

export function isHttpUrl(value) {
    return /^https?:\/\//i.test(String(value || '').trim());
}

export function projectLogoSrc(project) {
    const baseLogo = typeof project?.base_logo === 'string' ? project.base_logo.trim() : '';
    if (baseLogo) return baseLogo;
    const logo = typeof project?.logo === 'string' ? project.logo.trim() : '';
    if (/^(https?:|data:|blob:)/i.test(logo)) return logo;
    return '';
}

function projectEmailPreferences(emailPreferences, projectId) {
    const match = emailPreferences?.project_preferences?.find(project => project.project_id === projectId);
    return match?.preferences || {};
}

export function buildForm(project, emailPreferences) {
    const preferences = projectEmailPreferences(emailPreferences, project?.id);
    const descriptionOriginal = project?.description || '';
    const profiles = LEADERSHIP_ROLES.filter(role => hasValue(project?.[role.key])).map(role => ({
        role: role.key,
        url: String(project[role.key]).trim(),
    }));

    return {
        project_name: project?.project_name || '',
        company_name: project?.company_name || '',
        project_type: project?.project_type || '',
        project_deadline: toDateInputValue(project?.project_deadline),
        status: project?.status || '',
        investment_type: project?.investment_type || '',
        sector: project?.sector || '',
        customer_stage: project?.customer_stage || '',
        company_size: project?.company_size || '',
        established_year: project?.established_year ? String(project.established_year) : '',
        customer_type: project?.customer_type || '',
        customer_segment: project?.customer_segment || '',
        currency: currencyCode(project?.currency),
        revenue: project?.revenue ? String(project.revenue) : '',
        companieshouse: hasValue(project?.companieshouse) ? String(project.companieshouse) : '',
        description: toPlainText(descriptionOriginal),
        descriptionOriginal,
        descriptionOriginalPlain: toPlainText(descriptionOriginal),
        engagement_objective: project?.engagement_objective || '',
        scope_areas: Array.isArray(project?.scope_areas) ? project.scope_areas : [],
        website: hasValue(project?.website) ? project.website : '',
        product_website: hasValue(project?.product_website) ? project.product_website : '',
        linkedin: hasValue(project?.linkedin) ? project.linkedin : '',
        crunchbase: hasValue(project?.crunchbase) ? project.crunchbase : '',
        glassdoor: hasValue(project?.glassdoor) ? project.glassdoor : '',
        endole: hasValue(project?.endole) ? project.endole : '',
        profiles,
        builtwith: hasValue(project?.builtwith) ? project.builtwith : '',
        securitytrails: hasValue(project?.securitytrails) ? project.securitytrails : '',
        gtmetrix: hasValue(project?.gtmetrix) ? project.gtmetrix : '',
        similarweb: hasValue(project?.similarweb) ? project.similarweb : '',
        competitors: Array.isArray(project?.competitors) ? project.competitors.filter(hasValue) : [],
        projectUpdateEmails: Boolean(preferences[PROJECT_UPDATE_EMAIL]),
        reviewReminderEmails: Boolean(preferences[REVIEW_REMINDER_EMAIL]),
        emailOriginal: {
            [PROJECT_UPDATE_EMAIL]: Boolean(preferences[PROJECT_UPDATE_EMAIL]),
            [REVIEW_REMINDER_EMAIL]: Boolean(preferences[REVIEW_REMINDER_EMAIL]),
        },
    };
}

export function summaryIsValid(form) {
    return Boolean(form.project_name.trim() && form.project_type && parseProjectDate(form.project_deadline));
}

function descriptionPayload(form) {
    const current = form.description.trim();
    if (current === form.descriptionOriginalPlain) return form.descriptionOriginal;
    return current;
}

function leadershipPayload(form) {
    const payload = { ceo: '', coo: '', cto: '', cpo: '' };
    form.profiles.forEach(profile => {
        if (payload[profile.role] !== undefined) {
            payload[profile.role] = profile.url.trim();
        }
    });
    return payload;
}

function sectionProjectData(section, form) {
    switch (section) {
        case SECTION.summary:
            return {
                project_name: form.project_name.trim(),
                company_name: form.company_name.trim(),
                project_type: form.project_type,
                project_deadline: format(parseProjectDate(form.project_deadline), 'dd-MM-yyyy'),
            };
        case SECTION.organisation:
            return {
                sector: form.sector,
                customer_stage: form.customer_stage,
                company_size: form.company_size,
                established_year: form.established_year.trim() === '' ? null : Number(form.established_year),
                customer_type: form.customer_type,
                customer_segment: form.customer_segment,
                currency: currencySymbol(form.currency),
                revenue: form.revenue.trim() === '' ? 0 : Number(form.revenue),
                companieshouse: form.companieshouse.trim(),
                description: descriptionPayload(form),
            };
        case SECTION.engagement:
            return {
                engagement_objective: form.engagement_objective,
                scope_areas: form.scope_areas,
            };
        case SECTION.publicProfiles:
            return {
                website: form.website.trim(),
                product_website: form.product_website.trim(),
                linkedin: form.linkedin.trim(),
                crunchbase: form.crunchbase.trim(),
                glassdoor: form.glassdoor.trim(),
                endole: form.endole.trim(),
            };
        case SECTION.leadership:
            return leadershipPayload(form);
        case SECTION.technology:
            return {
                builtwith: form.builtwith.trim(),
                securitytrails: form.securitytrails.trim(),
                gtmetrix: form.gtmetrix.trim(),
                similarweb: form.similarweb.trim(),
            };
        case SECTION.competitors:
            return {
                competitors: form.competitors.map(item => item.trim()).filter(Boolean),
            };
        case SECTION.configuration:
            return {};
        default: {
            const unknownSection = section;
            throw new Error(`Unknown project information section: ${unknownSection}`);
        }
    }
}

function emailChanges(form) {
    const next = {
        [PROJECT_UPDATE_EMAIL]: form.projectUpdateEmails,
        [REVIEW_REMINDER_EMAIL]: form.reviewReminderEmails,
    };
    const changed = {};
    Object.entries(next).forEach(([key, value]) => {
        if (Boolean(form.emailOriginal?.[key]) !== value) changed[key] = value;
    });
    return changed;
}

export function buildSaveRequest(section, form) {
    const sections = section
        ? [section]
        : [
              SECTION.summary,
              SECTION.organisation,
              SECTION.engagement,
              SECTION.publicProfiles,
              SECTION.leadership,
              SECTION.technology,
              SECTION.competitors,
              SECTION.configuration,
          ];
    const data = {};
    sections.forEach(key => {
        Object.assign(data, sectionProjectData(key, form));
    });
    const includeEmail = !section || section === SECTION.configuration;
    return {
        data,
        email: includeEmail ? emailChanges(form) : {},
    };
}

export function saveError(section, form) {
    const checksSummary = !section || section === SECTION.summary;
    const checksOrganisation = !section || section === SECTION.organisation;
    if (checksSummary && !summaryIsValid(form)) {
        return 'Project name, project type, and project deadline are required.';
    }
    if (checksOrganisation && form.established_year.trim() !== '' && !/^\d{4}$/.test(form.established_year.trim())) {
        return 'Year established must be a four-digit year.';
    }
    if (checksOrganisation && form.revenue.trim() !== '' && Number.isNaN(Number(form.revenue))) {
        return 'Revenue must be a number.';
    }
    return '';
}
